import DynamicBrief from '@99designs/apps/spa/pages/create/briefs/DynamicBrief';
import ExternalBriefTransition, {
    getPartnerFromQueryParam,
} from '@99designs/apps/spa/pages/create/common/components/ExternalBriefTransition';
import LaunchBrief from '../mutations/LaunchBrief';
import { Loading } from '@99designs/spa-common';
import React from 'react';
import { launchedBriefUrl } from '@99designs/apps/spa/pages/create/util/routes';
import { Navigate, useParams } from 'react-router-dom';
import { useBriefQuery } from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import { linkTo } from '@99designs/routing';
import { useUrlQuery } from '@99designs/spa-common';

export type BriefParams = {
    briefId: string;
    step: string;
};

export default function Brief() {
    const { briefId } = useParams<BriefParams>() as BriefParams;

    const { fromPartner } = useUrlQuery();
    const partner = getPartnerFromQueryParam(fromPartner);

    const { loading, error, data } = useBriefQuery({
        fetchPolicy: 'no-cache',
        variables: {
            id: briefId,
        },
    });

    if (loading) {
        return <Loading size={'small'} />;
    }

    if (error) {
        throw error;
    }

    if (!data) {
        throw new Error('Failed to fetch data');
    }

    if (data.brief.state === 'LAUNCHED') {
        return <Navigate replace to={launchedBriefUrl(data.brief)} />;
    }

    const { brief } = data;

    const showPartnerTransition = !!partner;

    // Briefs created via the API render an interstitial page to ease
    // the transition from an external platform into 99designs.
    if (showPartnerTransition) {
        // Launch the brief immediately if it's been created via the API with a designer
        if (brief.designers.find((designer) => designer.source === 'API')) {
            return (
                <LaunchBrief brief={brief}>
                    {({ data }) => {
                        return (
                            <ExternalBriefTransition
                                id={brief.id}
                                designerCount={brief.designers.length}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                partner={partner}
                            >
                                {data && (
                                    <Navigate
                                        replace
                                        to={linkTo.project(
                                            `${data.launchStructuredBrief.projectIds[0]}`
                                        )}
                                    />
                                )}
                            </ExternalBriefTransition>
                        );
                    }}
                </LaunchBrief>
            );
        }

        return (
            <ExternalBriefTransition
                id={brief.id}
                designerCount={brief.designers.length}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                partner={partner}
            >
                <DynamicBrief />
            </ExternalBriefTransition>
        );
    }

    return <DynamicBrief />;
}
